import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { todayISO } from "../lib/format";
import { VoiceInput } from "./VoiceInput";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export const CATEGORIES = ["Mercado", "Contas", "Transporte", "Saúde", "Salário", "Lazer", "Geral"];
const emptyForm = { description: "", amount: "", category: "Geral", tipo: "despesa", date: todayISO() };

export const ExpenseFormDialog = ({ open, onOpenChange, expense = null }) => {
    const qc = useQueryClient();
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const editId = expense?.id || null;

    useEffect(() => {
        if (!open) return;
        if (expense) {
            setForm({
                description: expense.description || "",
                amount: expense.amount != null ? String(expense.amount) : "",
                category: expense.category || "Geral",
                tipo: expense.tipo || "despesa",
                date: (expense.date || "").slice(0, 10) || todayISO(),
            });
        } else {
            setForm(emptyForm);
        }
    }, [open, expense]);

    const onVoiceResult = (fields) => {
        setForm((f) => ({
            ...f,
            description: fields.description ?? f.description,
            amount: fields.amount != null ? String(fields.amount) : f.amount,
            category: fields.category && CATEGORIES.includes(fields.category) ? fields.category : f.category,
            tipo: fields.tipo === "receita" ? "receita" : "despesa",
        }));
    };

    const save = async () => {
        if (!form.description || !form.amount) { toast.error("Preencha descrição e valor"); return; }
        setSaving(true);
        try {
            if (editId) {
                await api.put(`/expenses/${editId}`, { ...form, amount: parseFloat(form.amount) });
            } else {
                await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
            }
            qc.invalidateQueries({ queryKey: ["expenses"] });
            qc.invalidateQueries({ queryKey: ["summary"] });
            onOpenChange(false);
            toast.success(editId ? "Lançamento atualizado" : "Lançamento adicionado");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-2xl sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-heading text-2xl font-medium">{editId ? "Editar lançamento" : "Novo lançamento"}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <VoiceInput formType="expense" onResult={onVoiceResult} label="Lançar por voz" />
                    <div>
                        <Label>Descrição</Label>
                        <Input data-testid="expense-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-xl" placeholder="Ex: Compras no mercado" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Valor (R$)</Label>
                            <Input data-testid="expense-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-xl font-mono-nums" placeholder="0,00" />
                        </div>
                        <div>
                            <Label>Data</Label>
                            <Input data-testid="expense-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Categoria</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger className="mt-1 rounded-xl" data-testid="expense-category"><SelectValue /></SelectTrigger>
                                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Tipo</Label>
                            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                                <SelectTrigger className="mt-1 rounded-xl" data-testid="expense-tipo"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="despesa">Despesa</SelectItem>
                                    <SelectItem value="receita">Receita</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="save-expense-btn">
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editId ? "Salvar alterações" : "Salvar lançamento"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
